/**
 * The record pages' shared furniture: the underlined tab row and the
 * label-first property rows.
 *
 * WEB TIER A · WT5 — RELOCATED VERBATIM from `features/inquiries/panel.tsx`,
 * which was deleted with the slide-over kill. The plan's recon (§1) recorded
 * these as "used by the three panels only"; that was STALE — by build time
 * `CrfRecordPage`, `ConfirmationEntryPage` and `SalesOrderRecordPage` had
 * adopted them across the feature boundary, and `InquiryRecordPage` (which
 * survives the kill) uses all three. So the SlideOver and its panel-only
 * pieces (`PanelSection`, the shadows, the scrim) died with their file, and
 * these moved here unchanged.
 *
 * They are LEGACY grammar, not house grammar: the Tier A house replacements
 * are `KvList`/`KvRow` (`components/ui`) for the rows and the record cards'
 * own headings for the sections. Migrating the remaining consumers is the
 * Tier B register/record sweep (docs/WEB-TIER-A-LEDGER.md) — folding it into
 * WT5 would have restyled three pages outside the cluster's scope.
 *
 * Original measurements (from the panel file, kept with the code):
 *  · `Tabs` (12:6421) — `Body/13`, a 2px `accent/indigo` underline on the
 *    active one.
 *  · `Record/PropertiesPanel` (I12:6449;8:43) — `TableHeader/11` group label,
 *    then rows of a 104px `Caption/12` `text/muted` name against a `Body/13`
 *    `text/primary` value.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** `Tabs` (12:6421) — `Body/13`, a 2px `accent/indigo` underline on the active one. */
export function PanelTabs<T extends string>({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { id: T; label: string; count?: number | null }[];
  active: T;
  onSelect: (id: T) => void;
}): React.JSX.Element {
  return (
    <div role="tablist" className="flex items-start gap-5 border-b border-line px-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          onClick={() => onSelect(tab.id)}
          className="flex flex-col items-start"
        >
          <span
            className={cn(
              'flex items-center gap-1.5 pb-2.5 pt-3 text-body13',
              tab.id === active ? 'text-indigo' : 'text-fg-secondary hover:text-fg',
            )}
          >
            {tab.label}
            {tab.count === undefined || tab.count === null ? null : (
              <span className="tnum text-label text-fg-muted">{tab.count}</span>
            )}
          </span>
          <span
            aria-hidden="true"
            className={cn('h-0.5 w-full', tab.id === active ? 'bg-indigo' : 'bg-transparent')}
          />
        </button>
      ))}
    </div>
  );
}

/** The `Label/11` section heading ("CUSTOMER & CONTACT", "TALLY LICENSE", …). */
export function SectionLabel({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="text-th uppercase text-fg-muted">{children}</p>;
}

export function PropertyGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2.5">
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

export function PropertyRow({
  name,
  children,
  mono = false,
}: {
  name: string;
  children: ReactNode;
  mono?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-start gap-2">
      <span className="w-[104px] shrink-0 text-label text-fg-muted">{name}</span>
      <span
        className={cn(
          'min-w-0 flex-1 text-body13 text-fg',
          // `Row/Tally Serial` (I12:6449;8:56) is the design's one mono value; the same
          // treatment is used here for the document number, which is the identifier an
          // operator reads out loud and compares character by character.
          mono && 'font-mono text-mono',
        )}
      >
        {children}
      </span>
    </div>
  );
}
